// F1 — **HER LEAD-COLLECTION MACHINE: THE THREE DOORS, DECLARED ONCE.**
//
// Her order: one standalone page, **NOT REACHABLE FROM THE SITE** (no menu entry, no internal link,
// direct URL only), serving all three funnels through a parameter, phone-first because the traffic is
// Instagram. The hook differs by which door they came through and so, later, does the reading.
//
// **THIS IS A `.js` LAW AND NOT A `src/lib/*.ts` MODULE, FOR `strings.js`' OWN REASON: THE SERVER
// COMPOSES FROM IT TOO.** The funnel email, the lead row's `source` column and her analytics buckets
// are all server-side, and `src/lib/*.ts` is CLIENT-ONLY — so a roster written there would be copied
// on the server within one round, and a second answer to *which funnels exist* is a second answer to
// *which ad her lead came from*, which is the number she spends money against (R28/R29).
//
// **THE KEY IS THE CONTRACT WITH HER ADS AND MAY NEVER BE RE-SPELLED.** These three strings are in
// live advertising creative the moment she ships it; renaming one does not break a build, it breaks
// a campaign silently and every lead through that door lands under the fallback below.

/** Her three doors, in the order she wrote them. */
export const FUNNEL_KEYS = ["wealthy", "psychic", "timeline"];

/**
 * **AN UNROSTERED OR ABSENT `f` RESOLVES HERE, AND THE CHOICE IS DECLARED RATHER THAN DEFAULTED
 * INTO.** Three facts arrive at this page (R18): a rostered key, NO key at all, and a key nobody
 * rosters. Only the first is an ad landing — her three URLs all carry the parameter — so the other
 * two are a typo, a truncated share or somebody poking at `/funnel`.
 *
 * **A DEAD END IS THE ONE ANSWER THAT COSTS HER SOMETHING**, so they render a real door rather than a
 * chooser (a fourth surface she did not order) or an error (a paid click thrown away). It is the
 * FIRST door she listed.
 *
 * **AND THERE IS EXACTLY ONE ANSWER DOWNSTREAM:** whatever this resolves to is what the page renders
 * AND what the lead is filed under, so a lead's source is always the door the visitor actually saw.
 * Resolving the copy one way and the attribution another would put a number in her report that no
 * screen ever showed anybody.
 */
export const FUNNEL_FALLBACK = "wealthy";

export function funnelFromParam(raw) {
  const key = String(raw ?? "").trim().toLowerCase();
  return FUNNEL_KEYS.includes(key) ? key : FUNNEL_FALLBACK;
}

/** Did the visitor actually name a door? Kept separate from the resolution above, because *she
 *  arrived through the psychic ad* and *she typed the bare URL* are two different facts and only the
 *  first is worth anything in a report (R18). Nothing renders differently; her analytics can. */
export function funnelWasNamed(raw) {
  const key = String(raw ?? "").trim().toLowerCase();
  return FUNNEL_KEYS.includes(key);
}

/**
 * **THE HOOK — HER TITLE IN GOLD CHROME, AND NOTHING UNDER IT.**
 *
 * Her titles are quoted from her order verbatim and are not to be "improved": they are the words in
 * the ad, and a landing page whose headline does not match the ad the click came from is the oldest
 * way there is to lose a paid visitor.
 *
 * **L2 (her live-test order, 2026-08-13) — THE `promise` SUBTITLE IS DELETED AT ITS DECLARATION.**
 * Her words after her daughter walked the live page: *"the subtitle line 'Your chart carries a money
 * story…' is REMOVED entirely — the title and the form carry the page."* It is removed HERE rather
 * than merely unrendered (DL-2/DT-2b/R181), because a retired string still sitting in this table is
 * one line away from being drawn again by a round that finds it and assumes it is wanted — and the
 * next reader cannot tell which of two answers the page wears.
 */
export const FUNNEL_COPY = {
  wealthy: { title: "Were You Born to Be Wealthy?" },
  psychic: { title: "How Psychic Are You?" },
  timeline: { title: "See Your Wealth Timeline" },
};

/** The copy for a resolved key. Total over the roster by construction — the fixture holds that as a
 *  biconditional, so a fourth door joins by going RED rather than by rendering a blank headline. */
export function funnelCopy(key) {
  return FUNNEL_COPY[funnelFromParam(key)];
}

/**
 * **FUNNEL-FIX-1 clause 1 — THE TWO LINES THAT EARN THE FORM.**
 *
 * Her words: one short warm explanation of WHY beside the form, and the safety line — *"small,
 * classy, gold on the house dark, NOT a legal wall, a hand extended."*
 *
 * >>> THEY ARE ONE DECLARATION FOR ALL THREE DOORS, AND THAT IS THE WHOLE POINT OF PUTTING THEM
 * HERE. <<< A safety promise typed per funnel is three promises, and the one that drifts is the one
 * a stranger reads before deciding whether to hand over her birth date. The TITLE is per door
 * because it has to match the ad the click came from; these do not vary at all.
 *
 * **THE WHY IS TRUE OF THE CODE AND IS NOT A CLAIM ANYBODY HAD TO INVENT** (R141): this rail really
 * does cast the visitor's own chart from her own birth details, and if it cannot cast one it REFUSES
 * rather than shipping a horoscope with her name on it.
 *
 * **THE SAFETY LINE IS A PROMISE THE HOUSE CAN KEEP**, which is why it says what it says and not
 * more: *we do not sell it* is true — nothing anywhere sells a lead — while *nobody ever sees it*
 * would be false the moment she mails her own list. A trust line that is not exactly true is worse
 * than no trust line at all (R141).
 */
export const FUNNEL_WHY = "We are pulling your birth chart and reading it against where the sky is"
  + " right now. That is how the reading gets this accurate.";
export const FUNNEL_SAFETY = "Your information is safe here. We do not sell it, ever.";

/**
 * **FUNNEL-FIX-1 clause 4 — THE OTHER READINGS AS TAPPABLE QUESTION CARDS.**
 *
 * Her ruling: the empty side space becomes conversion blocks — *"the other readings as tappable
 * question cards, each landing on SIGNUP with the continuity pre-fill"* — and *"EVERYTHING TAPPABLE:
 * the dead teaser text becomes real doors or dies."*
 *
 * >>> THE QUESTION IS NOT TYPED HERE. IT IS THE HOUSE'S OWN PROMISE, DERIVED (R28/R29). <<< Every
 * one of these is already a card on her home page with a promise she wrote, and a second wording of
 * *"What's blocking my money?"* is a second promise — with the one that drifts being the one a
 * stranger is looking at at the moment she decides. homeTapMap.js imports NOTHING, so this stays a
 * module a .mjs gate can load and run whole.
 *
 * **AND A CARD MAY ONLY NAME A READING THIS HOUSE HAS REALLY BUILT.** The roster is held against
 * READING_CARDS by the fixture, so a card cannot promise a reading that does not exist — which is
 * the debt HOME-1 shipped with and had to declare (four cards promised on the front door with
 * nothing behind them), and it is far worse here, where the visitor has just been shown proof the
 * house knows her.
 *
 * **THE DESTINATION IS SIGNUP AND NEVER THE READING**, which is HOME-1's own standing ruling on this
 * exact question: *"these are PAID doors, not free funnels"*. A card that shortcut to its reading
 * would hand a stranger a paid door and meet her with a wall at the end of it.
 */
export const FUNNEL_CARD_READINGS = [
  "whats-blocking-my-money",
  "how-they-really-feel",
  "how-psychic-are-you",
];

export function funnelQuestionCards(cards) {
  const rows = Array.isArray(cards) ? cards : [];
  return FUNNEL_CARD_READINGS
    .map((reading) => {
      const card = rows.find((c) => c && c.reading === reading);
      // A reading the roster cannot find is DROPPED rather than drawn with its key as its words —
      // R25's `DRAWER_ICONS` ruling: an unrostered label falling through to a plausible wrong answer
      // is worse than a visibly missing one, and here the wrong answer would be machine text on the
      // one screen that has just proved the house is real.
      return card && card.promise ? { reading, question: card.promise } : null;
    })
    .filter(Boolean);
}

/** Her ads point here. ONE path, three URLs — reported to her rather than assembled by hand at three
 *  call sites, because a query string typed into ad creative is not something anybody can gate. */
// ================================================================================================
// >>> **HER THREE APPROVED REVIEWS, UNDER THE FOLD ON THE LANDER (LANDER-1, 2026-08-23).** <<<
//
// A3's clause is *"THREE approved reviews under the fold and NOTHING ELSE"*, and the lander showed
// **ZERO** — measured on the painted page at 393x852, all three doors. The pool was fetched only
// once a paragraph existed to sit under (FUNNEL-FIX-1's own speed decision), so the one surface her
// advertising pays to bring people to had no proof on it at all.
//
// **THE COUNT IS A DECLARED NUMBER RATHER THAN A SLICE AT THE CALL SITE**, because the pool is up to
// SIXTY (`showableReviews` LIMIT 60) and a lander that painted sixty is the *"NOTHING ELSE"* half of
// her own sentence broken by the half above it.
//
// **PRESENCE, NEVER COERCION (R53/R136/R137):** these arrive off a payload, where `String(123)` is a
// perfectly plausible-looking quote and `Boolean("false")` is true. Only a real non-empty STRING is
// a review, and anything that is not a list at all answers NONE rather than throwing on the render
// path of a page a stranger just paid a click to reach (R19: a crash is not an empty pool).
//
// **AND AN EMPTY ANSWER DRAWS NOTHING RATHER THAN A HEADING OVER IT** (T9-2's no-body-no-box law) —
// which is also the third state's whole job here: *not arrived*, *arrived empty* and *arrived* are
// three facts (R18/R65), and a STRANGER IS NEVER TOLD SOMETHING BROKE (her conversion surface 1's
// own ruling, word for word). The first two draw the same nothing and only the third paints.
export const LANDER_REVIEW_COUNT = 3;
export function landerReviews(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (const row of rows) {
    const quote = row && typeof row === "object" ? row.quote : null;
    if (typeof quote !== "string" || !quote.trim()) continue;
    out.push(quote);
    if (out.length >= LANDER_REVIEW_COUNT) break;
  }
  return out;
}

export const FUNNEL_PATH = "/funnel";
export const funnelUrl = (key, origin = "https://12thhouse.ai") =>
  `${String(origin).replace(/\/+$/, "")}${FUNNEL_PATH}?f=${funnelFromParam(key)}`;
